import { inject, Injector } from '@angular/core';
import type { CanActivateFn } from '@angular/router';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { AppPathsMap } from '@core/routing/app-paths';
import { signalToObservable } from '@core/utils/signal.utilities';
import {
  createAsyncErrorHandler,
  createErrorMetadata,
  ErrorSeverityMap,
} from '@kerberos-compliance/compliance-shared-ui';
import { combineLatest, lastValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AuthStore } from '@/auth/store/auth.store';
import { ContractFacade } from '@/contract/facades/contract.facade';
import type { GuardFactoryOptions } from '@/contract/types/contract.types';

/**
 * Factory function to create a route guard for contract-specific routes.
 *
 * @param {GuardFactoryOptions} options - Configuration options for the guard.
 * @param {GuardFactoryOptions['allow']} options.allow - Function determining if the route should be accessible
 * based on the selected contract and associated error. It receives an object containing:
 *  - `selected`: The selected contract data.
 *  - `error`: Any errors encountered while fetching the contract.
 * Returns a boolean to indicate access permission.
 * @param {string} options.redirectOnDenied - Route path to navigate to if access is denied.
 * @param {string} options.componentName - Name of the component associated with this guard,
 * used for error tracking and handling.
 * @returns {CanActivateFn} A function that determines if a user can activate a route.
 * It checks if the user is logged in, validates access based on contract and permissions,
 * and handles navigation and errors accordingly.
 */
const contractGuardFactory =
  ({ allow, redirectOnDenied, componentName }: GuardFactoryOptions): CanActivateFn =>
  async () => {
    const authStore = inject(AuthStore);
    const navigationService = inject(NavigationService);
    const contractFacade = inject(ContractFacade);
    const injector = inject(Injector);
    const asyncErrorHandler = createAsyncErrorHandler();

    // Check if user is logged in
    const isUserLoggedInResult = await asyncErrorHandler(
      async () => {
        const isUserLoggedIn$ = signalToObservable(injector, authStore.isUserLoggedIn);
        const isLogged = await lastValueFrom(isUserLoggedIn$.pipe(take(1)));

        if (!isLogged) {
          void navigationService.navigateTo('logout');
          return false;
        }
        return true;
      },
      {
        metadata: createErrorMetadata(componentName, 'Error fetching user login status', ErrorSeverityMap.High),
        silent: true,
        onError: () => void navigationService.navigateTo('logout'),
      },
    );

    if (!isUserLoggedInResult) {
      return false;
    }

    const contractCheckResult = await asyncErrorHandler(
      async () => {
        const loading$ = signalToObservable(injector, contractFacade.loading);
        const selectedContract$ = signalToObservable(injector, contractFacade.selectedContract);
        const error$ = signalToObservable(injector, contractFacade.error);

        const [, selected, error] = await lastValueFrom(
          combineLatest([loading$, selectedContract$, error$]).pipe(
            filter(([loading, selected_, error_]) => !loading && (!!selected_ || !!error_)),
            take(1),
          ),
        );

        const isAllowed = allow({ selected: selected, error });
        if (!isAllowed) {
          void navigationService.navigateTo([redirectOnDenied]);
          return false;
        }
        return true;
      },
      {
        metadata: createErrorMetadata(componentName, 'Error fetching contract', ErrorSeverityMap.High),
        silent: true,
        onError: () => void navigationService.navigateTo('logout'),
      },
    );

    return contractCheckResult ?? false;
  };

/**
 * A route guard function
 * that ensures the user's contract is active and service is active before allowing access to a route.
 *
 * This function performs the following checks:
 * - Verifies if the user is logged in by selecting `selectIsUserLoggedIn` from the store.
 * - Waits for the contract details and grants to load from the `ContractStore`.
 * - Checks if there was an error in loading the contract,
 * if the selected contract is available, and if the service is active.
 * - Redirects users to the `contractExpired` route if the active contract conditions are not met.
 * - Redirects users to the `logout` route if an error occurs during login status validation or contract validation steps.
 *
 * If all checks pass, it grants access to the route.
 *
 * Errors encountered during the process are handled by the `AmlDeskErrorHandlerService`.
 *
 * @type {CanActivateFn}
 */
export const contractActiveGuard: CanActivateFn = contractGuardFactory({
  componentName: 'contractActiveGuard',
  redirectOnDenied: AppPathsMap.ContractExpired,
  allow: ({ selected, error }) => !!selected?.isActive && !error,
});

/**
 * A route guard function created to check the status of a contract and manage navigation permissions.
 * The guard determines if the route can be activated based on the contract's activity status.
 * Redirects to a predefined route if access is denied.
 *
 * This guard allows navigation when the selected entity either lacks a contract
 * or has a contract that is not active.
 *
 * Properties configured for this route guard:
 * - componentName: Identifies the guard as "contractInactiveGuard".
 * - redirectOnDenied: Defines the fallback route to navigate to if access is denied (e.g., dashboard).
 * - allow: A predicate function that examines the "selected" entity and permits navigation
 *          if the "contract" is absent or its "serviceActive" property is false.
 *
 * Used for route protection in scenarios where inactive or missing contracts
 * are expected to allow user navigation.
 *
 * @type {CanActivateFn}
 */
export const contractInactiveGuard: CanActivateFn = contractGuardFactory({
  componentName: 'contractInactiveGuard',
  redirectOnDenied: AppPathsMap.Dashboard,
  allow: ({ selected }) => !selected?.isActive,
});
